import { type Uuid, type MangoSelector, PublishStatus } from "luminary-shared";
import { sessionNow } from "./sessionNow";

export type MangoIsPublishedOptions = {
    /**
     * When true, also matches scheduled content: `publishDate` in the future is allowed
     * if `parentShowComingSoon === true` (for list/carousel "Coming soon" tiles).
     * Default true: set to false to require publishDate <= now.
     */
    includeScheduled?: boolean;
};

/**
 * Builds the Mango selector conditions for "is this doc published right now" —
 * status, publish date, and expiry — WITHOUT any language selection. This is the
 * single source of truth for that check: reused by `mangoIsPublished` below (which
 * adds language-priority selection on top, for feed queries that want one doc per
 * parent) and by callers that need every published doc regardless of language (e.g.
 * a sibling-translations list), who can compile it to a plain predicate via
 * `mangoCompile` from `luminary-shared`.
 *
 * @param options - Optional `includeScheduled` for upcoming/coming-soon display in feeds
 * @returns Array of Mango selector conditions to be used in an $and clause
 */
export function publishedNowConditions(options?: MangoIsPublishedOptions): MangoSelector[] {
    // Session reference time — a ref captured at page load and advanced only when
    // live-sync delivers newly published content (see sessionNow.ts). NOT a live
    // `Date.now()`: the bound is embedded in the selector, which HybridQuery uses
    // as its reactive dedup key, so a live clock would re-key the query on every
    // tracked-ref change and re-fire the API supplement POST. The ref stays
    // byte-stable between bumps, so the dedup key is unaffected in the idle case.
    const now = sessionNow();
    const includeScheduled = options?.includeScheduled !== false;

    // Published content always carries a numeric publishDate (API-enforced via
    // @IsOptionalIf(status===Draft) on ContentDto.publishDate, and these clauses are
    // always AND-ed with status===Published below). So we test publishDate directly
    // rather than also matching missing/null — those branches could never co-occur
    // with status===Published and only defeated CouchDB's publishDate index range.
    const publishDateSelector: MangoSelector = includeScheduled
        ? // Already-published (publishDate <= now) OR a scheduled "coming soon" doc.
          { $or: [{ publishDate: { $lte: now } }, { parentShowComingSoon: true }] }
        : // Publish date must be at/before now.
          { publishDate: { $lte: now } };

    return [
        // Draft documents are synced to app clients (for future preview functionality),
        // so we must filter to only show published content in regular views.
        { status: PublishStatus.Published },
        publishDateSelector,
        // Expiry date check: either doesn't exist, is null, or is in the future
        {
            $or: [
                { expiryDate: { $exists: false } },
                { expiryDate: null },
                { expiryDate: { $gte: now } },
            ],
        },
    ];
}

/**
 * Builds Mango selector conditions for the "isPublished" check.
 * This can be used to inject the publication status, date, and language priority logic into a Mango query.
 *
 * The conditions check:
 * 1. status === "published"
 * 2. publishDate: by default <= now; with `includeScheduled`, also future dates when `parentShowComingSoon`
 *    (published content always has a publishDate, so missing/null is not matched)
 * 3. expiryDate is either missing, null, or >= now (not expired)
 * 4. language matches the first available language from the user's preferences
 *
 * @param languageIds - Array of language IDs in priority order (most preferred first)
 * @param options - Optional `includeScheduled` for upcoming/coming-soon display in feeds
 * @returns Array of Mango selector conditions to be used in an $and clause
 *
 * @example
 * ```ts
 * const selector = {
 *     $and: [
 *         { type: DocType.Content },
 *         ...mangoIsPublished(appLanguageIds),
 *     ],
 * };
 * ```
 */
export function mangoIsPublished(languageIds: Uuid[], options?: MangoIsPublishedOptions): MangoSelector[] {
    return [
        ...publishedNowConditions(options),
        // Language priority: select the best available translation
        buildLanguagePrioritySelector(languageIds),
    ];
}

/**
 * Builds a Mango selector that matches documents where the language field
 * is the first available language from the priority list.
 *
 * For each language at index i, it checks that none of the higher-priority
 * languages (0 to i-1) are in availableTranslations.
 *
 * @param languageIds - Array of language IDs in priority order
 * @returns Mango selector for language priority matching
 */
function buildLanguagePrioritySelector(languageIds: Uuid[]): MangoSelector {
    // Filter out undefined/null values that might occur if array is shorter than expected
    const validLanguageIds = languageIds.filter((id) => id != null);

    if (validLanguageIds.length === 0) {
        // No languages specified, match any document
        return {};
    }

    const languageConditions: MangoSelector[] = validLanguageIds.map((langId, index) => {
        if (index === 0) {
            // First language: just match it directly
            return { language: langId };
        }

        // For subsequent languages: match only if higher-priority languages
        // are NOT available in the translations
        const higherPriorityNotAvailable = validLanguageIds.slice(0, index).map((prevLangId) => ({
            $not: { availableTranslations: { $elemMatch: { $eq: prevLangId } } },
        }));

        return {
            $and: [{ language: langId }, ...higherPriorityNotAvailable],
        };
    });

    // Fallback: if none of the preferred languages are available,
    // return the first available translation (any language)
    const fallbackCondition: MangoSelector = {
        $and: validLanguageIds.map((langId) => ({
            $not: { availableTranslations: { $elemMatch: { $eq: langId } } },
        })),
    };

    return {
        $or: [...languageConditions, fallbackCondition],
    };
}
